import { HTMLAttributes, useRef } from 'react';
import { latoBold } from '@/helpers/fonts';

interface ButtonBaseProps extends HTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary';
  themeTextColor?: string;
  themeBackgroundColor?: string;
}

type ButtonAsButton = ButtonBaseProps &
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    as?: 'button';
  };

type ButtonAsLink = ButtonBaseProps &
  React.AnchorHTMLAttributes<HTMLAnchorElement> & {
    as: 'a';
  };

type ButtonProps = ButtonAsButton | ButtonAsLink;

export default function Button({
  as = 'button',
  variant = 'primary',
  children,
  className,
  themeTextColor = '',
  themeBackgroundColor = '',
  ...rest
}: ButtonProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleClick = () => {
    // Search for the first link inside the button and click on it
    const link = buttonRef.current?.querySelector(
      'a, [role="link"]'
    ) as HTMLElement;
    if (link) {
      link.click();
    }
  };

  if (as === 'a') {
    return (
      <a
        className={`${latoBold.className} ${
          variant === 'primary'
            ? 'text-zinc-800 bg-primary'
            : 'text-white bg-neutral-800'
        } font-semibold text-[16.89px] sm:text-[22px] sm:py-4 py-3 sm:px-12 px-9 ${className}`}
        style={{ color: themeTextColor, backgroundColor: themeBackgroundColor }}
        {...(rest as React.AnchorHTMLAttributes<HTMLAnchorElement>)} // cast correct
      >
        {children}
      </a>
    );
  }

  return (
    <button
      ref={buttonRef}
      onClick={handleClick}
      className={`${latoBold.className} ${
        variant === 'primary'
          ? 'text-zinc-800 bg-primary'
          : 'text-white bg-neutral-800'
      } font-semibold text-[16.89px] sm:text-[22px] sm:py-4 py-3 sm:px-12 px-9 ${className}`}
      {...(rest as React.ButtonHTMLAttributes<HTMLButtonElement>)}
      style={{
        color: themeTextColor,
        backgroundColor: themeBackgroundColor,
      }}
    >
      {children}
    </button>
  );
}
